// @owners { team: patients-team }
import { Body, Column, H1, LgPadding, MdSpacing, SmSpacing } from '@alto/design-system';
import React, { useEffect, useRef } from 'react';
import { getImage } from '~shared/assets/images/getImage';
import { IMG_ALT_TEXTS } from '~shared/constants';
import { type AnalyticsProps, SOURCES } from '~shared/features/courier-tips/AnalyticsProps';
import sendTipAnalyticsEvent from '~shared/features/courier-tips/actions/sendTipAnalyticsEvent';
import { COURIER_NEW_TIP_COPY, COURIER_TIP_COPY, INITIAL_TIP_AMOUNT } from '~shared/features/courier-tips/constants';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useDispatchShared } from '~shared/store';
import { ChoosePaymentMethod } from './ChoosePaymentMethod';
import CourierTipInput from './CourierTipInput';
import TipQualifiedExpenseWarning from './TipQualifiedExpenseWarning';
import { Image } from '~web/components/Image';

type Props = {
  readonly analyticsProps: AnalyticsProps;
  readonly editTipAmount: (tip: number) => void;
  readonly editTipPaymentMethodID: (arg0: number) => void;
  readonly tipAmount: number;
  readonly tipPaymentMethodID: number | null | undefined;
};

const EditOrAddTip = ({
  analyticsProps,
  editTipAmount,
  editTipPaymentMethodID,
  tipAmount,
  tipPaymentMethodID,
}: Props) => {
  const dispatch = useDispatchShared();
  const { source } = analyticsProps;
  const loaded = useRef(false);

  useEffect(() => {
    if (loaded.current) return;
    // leave tip as is if one is already present for the shipment
    if (!tipAmount && source !== SOURCES.EDIT_DELIVERY) {
      loaded.current = true;
      editTipAmount(INITIAL_TIP_AMOUNT);
    }
  }, [tipAmount, source, editTipAmount]);

  useEffect(() => {
    const eventKey =
      source === SOURCES.POST_DELIVERY
        ? EVENTS.COURIER_TIPS__POST_DELIVERY_TIP_VIEWED
        : EVENTS.COURIER_TIPS__EDIT_TIP_VIEWED;
    dispatch(
      sendTipAnalyticsEvent({
        eventKey,
        analyticsProps,
        additionalProps: { tip_amount: tipAmount },
      }),
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <LgPadding>
      <Column>
        <Image
          src={getImage('bikeCourierWithBackground')}
          alt={IMG_ALT_TEXTS.bikeCourier}
        />
        <H1 center>{COURIER_TIP_COPY.CARD_HEADER}</H1>
        <MdSpacing />
        <Body center>{COURIER_NEW_TIP_COPY.CARD_BODY}</Body>
        <MdSpacing />
        <CourierTipInput
          alignCenter
          editTipAmount={editTipAmount}
          initialTipAmount={tipAmount}
          analyticsProps={analyticsProps}
        />
        <MdSpacing />
        <ChoosePaymentMethod
          paymentMethodID={tipPaymentMethodID}
          selectPaymentMethodID={editTipPaymentMethodID}
        />
        <SmSpacing />
        <TipQualifiedExpenseWarning />
      </Column>
    </LgPadding>
  );
};

export default EditOrAddTip;
